<script setup lang="ts">
    import {ref, computed, inject, onMounted, onBeforeUnmount} from "vue";
    import type {VideoTrackingDataType} from "@songfinch/types";

    type DVideoPropTypes = {
        src: string;
        title: string;
        variant?: "fill-section";
        preload?: string;
        controls?: boolean;
        dataTestId?: string;
    }

    const videoPlayer = ref<HTMLVideoElement>();

    const srcType = computed(() => {
        const extension = props.src.split(".").pop()?.toLowerCase();
        switch (extension) {
        case "mp4":
            return "video/mp4";
        case "webm":
            return "video/webm";
        case "ogg":
            return "video/ogg";
        default:
            // Fallback to MP4
            return "video/mp4";
        }
    });

    const videoTrackEvent = inject<
        (
            e: Event | HTMLElement,
            type: "video_played" | "video_stopped",
            payload: VideoTrackingDataType,
        ) => void
    >("videoTrackEvent", () => {});

    const props = withDefaults(defineProps<DVideoPropTypes>(), {
        preload: "metadata",
        dataTestId: "DVideo",
        variant: undefined,
    });

    const trackVideo = (
        type: "video_played" | "video_stopped",
        e: Event | HTMLElement,
    ) => {
        if (!videoTrackEvent || !e || videoPlayer.value?.duration === undefined)
            return;

        let percentagePlayed;
        if (videoPlayer.value?.currentTime && videoPlayer.value?.duration) {
            percentagePlayed = Math.round(
                (videoPlayer.value?.currentTime / videoPlayer.value?.duration) * 100,
            );
        }

        const trackingObject: VideoTrackingDataType = {
            title: props.title,
            time_played: videoPlayer.value?.currentTime.toFixed(2) || undefined,
            video_duration: videoPlayer.value?.duration.toFixed(2) || undefined,
            percentage_played: percentagePlayed ? `${percentagePlayed}%` : undefined,
        };
        videoTrackEvent(e, type, trackingObject);
    };

    const togglePlay = () => {
        if (!videoPlayer.value) return;
        if (videoPlayer.value?.paused) {
            videoPlayer.value.play();
        } else {
            videoPlayer.value.pause();
        }
    };

    onMounted(() => {
        if (videoPlayer.value) {
            videoPlayer.value.addEventListener("play", (e) =>
                trackVideo("video_played", e),
            );
            videoPlayer.value.addEventListener("pause", (e) =>
                trackVideo("video_stopped", e),
            );
        }
    });

    onBeforeUnmount(() => {
        if (videoPlayer.value) {
            if (!videoPlayer.value?.paused) {
                trackVideo("video_stopped", videoPlayer.value);
            }

            videoPlayer.value.removeEventListener("play", (e) =>
                trackVideo("video_played", e),
            );
            videoPlayer.value.removeEventListener("pause", (e) =>
                trackVideo("video_stopped", e),
            );
        }
    });
</script>

<template>
    <video
        ref="videoPlayer"
        class="d-video"
        :data-test-id="dataTestId"
        :class="variant"
        :alt="title"
        :preload="preload"
        :controls="controls"
        @click="togglePlay"
    >
        <source :src="src" :type="srcType">
    </video>
</template>


<style scoped lang="scss">
.d-video {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain; 
    overflow: hidden;
    background-color: var(--soft-black);
}

.fill-section {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  max-height: 100%;
  object-fit: cover;
  z-index: -1;
}
</style>
